import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal, SpinningLoader } from "risa-oasis-ui_v2";
import BadgeDropdown from "../../../../../components/badgeDropdown/badgeDropdown";
import ImageViewerWithBadgesModal from "../../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../../../../data-model/badgeWithIconProps";
import { EVWriteBackStatus } from "../../../../../enums/evBvWriteBackStatus";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { EventsName } from "../../../../../enums/eventsName";
import { LocalStorageKeys } from "../../../../../enums/localStorageKeys";
import { ModalId } from "../../../../../enums/modalId";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import EyeIcon from "../../../../../svg/eye";
import { getItemFromLocalStorage } from "../../../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";
import { handleDisplayWriteBackTrailScreenshot } from "../actionHandlers/displayWriteBackScreenshot";
interface SingleCommentProps {
  id: string;
  writeBackComment: string;
  originalComment: string;
  boStatus: string;
  onBoStatusChange: (data: { id: string; status: string }) => void;
  onWriteBackCommentChange: (data: { id: string; comment: string }) => void;
  showWriteBackNote?: boolean;
  index: number;
}

const SingleComment = (props: SingleCommentProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const writeBackContainerRef = useRef<HTMLDivElement>(null);
  const [writeBackComment, setWriteBackComment] = useState("");
  const [boStatus, setBoStatus] = useState(props.boStatus);
  const [boStatusOptions, setBoStatusOptions] = useState<BadgeWithIconProps[]>(
    [],
  );
  const oldDataForLogEvent = useRef<string>(props.writeBackComment);

  const dispatch = useDispatch<AppDispatch>();

  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modal,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const logEventTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const adjustTextareaTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getEvWriteBackStatus = useMemo(() => {
    const status = singleMedicalPaOrder?.status?.ev_write_back_status;
    if (status === EVWriteBackStatus.IN_PROGRESS) {
      return "Initiated";
    }
    if (status === EVWriteBackStatus.SUCCESS) {
      return "Completed";
    }

    if (status === EVWriteBackStatus.ERROR) {
      return "Error";
    }

    if (status === EVWriteBackStatus.QUEUED) {
      return "Queued";
    }

    return "";
  }, [singleMedicalPaOrder]);

  const getTextColor = useMemo(() => {
    const status = singleMedicalPaOrder?.status?.ev_write_back_status;
    if (status === EVWriteBackStatus.IN_PROGRESS) {
      return "text-secondaryYellow-4";
    }
    if (status === EVWriteBackStatus.SUCCESS) {
      return "text-primaryGreen-4";
    }

    if (status === EVWriteBackStatus.ERROR) {
      return "text-tertiaryRed-4";
    }

    if (status === EVWriteBackStatus.QUEUED) {
      return "text-secondaryPurple-4";
    }
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    if (boOptions) {
      const boStatusOptions = boOptions.map((option) => ({
        id: option.value,
        text: option.label,
        bgColor: "#F5F5F5",
        textColor: "#0F0F0F",
      }));
      setBoStatusOptions(boStatusOptions);
    }
  }, [boOptions]);

  useEffect(() => {
    setWriteBackComment(props.writeBackComment);
  }, [props.writeBackComment]);

  const adjustTextareaHeight = useCallback(() => {
    if (adjustTextareaTimeoutRef.current) {
      clearTimeout(adjustTextareaTimeoutRef.current);
    }
    adjustTextareaTimeoutRef.current = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height =
          textareaRef.current.scrollHeight + "px";
        if (commentsContainerRef.current && writeBackContainerRef.current) {
          commentsContainerRef.current.style.height =
            writeBackContainerRef.current.scrollHeight + "px";
        }
      }
      adjustTextareaTimeoutRef.current = null;
    }, 100);
  }, []);

  const renderTextWithProperIndentation = (text: string) => {
    const formattedText = (text ?? "N/A").replace(/\\n/g, "\n");
    return (
      <>
        {formattedText.split(/\n{2,}/).map((paragraph, index) => (
          <p key={index} className="mb-4">
            {paragraph.split("\n").map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        ))}
      </>
    );
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [writeBackComment, props.showWriteBackNote, adjustTextareaHeight]);

  const debouncedLogEvent = useCallback((eventData: any) => {
    if (logEventTimeoutRef.current) {
      clearTimeout(logEventTimeoutRef.current);
    }
    logEventTimeoutRef.current = setTimeout(() => {
      apiWorkerManager.enqueueAndProcess(eventData);
      logEventTimeoutRef.current = null;
    }, 850);
  }, []);

  const handleSelectionLog = (badge: string) => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name:
        EventsName.RADIO_BUTTON_SELECTED +
        " (" +
        (props.index + 1) +
        " - " +
        badge +
        ")",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
        current_value: boStatus ?? "",
        old_value: singleMedicalPaOrder?.status?.bo_status ?? "",
        new_value: badge,
        payer_name: payerName,
      },
    });
  };

  const handleDefaultBadgeClick = () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name:
        EventsName.RADIO_BUTTON_CLICKED + " (" + (props.index + 1) + ")",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
        current_value: boStatus ?? "",
        payer_name: payerName,
      },
    });
  };

  useEffect(() => {
    if (openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL) {
      openModal(ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    return () => {
      if (adjustTextareaTimeoutRef.current) {
        clearTimeout(adjustTextareaTimeoutRef.current);
      }
      if (logEventTimeoutRef.current) {
        clearTimeout(logEventTimeoutRef.current);
      }
    };
  }, []);

  const jCodesFromLocalStorage = getItemFromLocalStorage(
    `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
  );
  const jCodes = jCodesFromLocalStorage?.join(",") ?? "";

  return (
    <div className="single-comment--container flex gap-5">
      <div
        className="single-comment-original flex-1 overflow-auto rounded-md bg-primaryGray-16 p-3 text-h12 font-normal"
        ref={commentsContainerRef}
      >
        {renderTextWithProperIndentation(props.originalComment)}
      </div>
      <div
        className={`single-comment-writeback h-fit flex-1 ${
          props.showWriteBackNote
            ? "rounded-md border border-primaryGray-14"
            : ""
        }`}
        id={`write-back-note-${props.id}`}
        ref={writeBackContainerRef}
      >
        {props.showWriteBackNote && (
          <>
            <div className="write-back-bo-status flex items-center gap-2 border-b border-primaryGray-14 p-3">
              <div className="write-back-bo-status--radio text-h12 font-normal">
                Radio Button :
              </div>
              <div className="write-back-bo-status--badge-dropdown">
                {false ? (
                  <>
                    <div className="loading-status--container flex items-center gap-2 text-sm">
                      <SpinningLoader />
                      <div className="italic text-primaryGray-5">
                        Loading Status List...
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <BadgeDropdown
                      onDefaultBadgeClick={(id) => {
                        handleDefaultBadgeClick();
                      }}
                      selectedBadge={
                        boStatusOptions.find(
                          (status) => status.id === boStatus,
                        ) || {
                          id: "",
                          text: "Select Status",
                          bgColor: "#F5F5F5",
                          textColor: "#0F0F0F",
                        }
                      }
                      badgeList={boStatusOptions
                        .sort((a, b) => a.id.localeCompare(b.id))
                        .filter((status) => status.id !== "")}
                      onClick={(badge) => {
                        handleSelectionLog(badge);
                        setBoStatus(badge);
                        props.onBoStatusChange({
                          id: props.id,
                          status: badge,
                        });
                      }}
                    />
                  </>
                )}
              </div>
              <div
                className={`ev-write-back status ml-auto text-tiny font-semibold italic ${getTextColor}`}
              >
                {getEvWriteBackStatus}
              </div>
              {getEvWriteBackStatus === "Completed" && (
                <EyeIcon
                  color="#008F32"
                  className="cursor-pointer"
                  onClick={() => {
                    handleDisplayWriteBackTrailScreenshot(
                      singleMedicalPaOrder?.id ?? "",
                      dispatch,
                      authStatusOptions ?? [],
                    );
                  }}
                />
              )}
            </div>
            <div
              className="write-back-note h-max p-3"
              onClick={() => {
                apiWorkerManager.enqueueAndProcess({
                  patient_id: singleMedicalPaOrder?.patient_id ?? "",
                  order_id: singleMedicalPaOrder?.order_id ?? "",
                  org_id: getOrgIdForFetchExternalWorklist() || "",
                  user_id: user?.id ?? "",
                  user_email: user?.email ?? "",
                  event_name:
                    EventsName.CLICKED_ON_BUSINESS_OFFICE +
                    " (" +
                    (props.index + 1) +
                    ")",
                  additional_data: {
                    screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
                    index: props.index ?? 0,
                    auth_status:
                      singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
                    j_code: jCodes,
                    payer_name: payerName,
                  },
                });
              }}
            >
              <textarea
                ref={textareaRef}
                className="w-full resize-none overflow-hidden text-h12 font-normal outline-none"
                value={writeBackComment}
                name={`write-back-note-${props.id}`}
                id={`write-back-note-${props.id}`}
                onChange={(e) => {
                  setWriteBackComment(e.target.value);
                  props.onWriteBackCommentChange({
                    id: props.id,
                    comment: e.target.value,
                  });
                  adjustTextareaHeight();
                  debouncedLogEvent({
                    patient_id: singleMedicalPaOrder?.patient_id ?? "",
                    order_id: singleMedicalPaOrder?.order_id ?? "",
                    org_id: getOrgIdForFetchExternalWorklist() || "",
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    event_name:
                      EventsName.BUSINESS_OFFICE_EDITED +
                      " (" +
                      (props.index + 1) +
                      ")",
                    additional_data: {
                      screen_name:
                        ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
                      new_value: e.target.value,
                      old_value: oldDataForLogEvent.current,
                      auth_status:
                        singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
                      payer_name: payerName,
                      coverage_status:
                        singleMedicalPaOrder?.status?.ev_bv?.primary ?? "",
                      j_code: jCodes,
                    },
                  });
                }}
              />
            </div>
          </>
        )}
      </div>
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
        />
      )}
    </div>
  );
};

export default SingleComment;
